import { useState } from "react";
import { X, Sparkles } from "lucide-react";
import { ResponsiveSheet } from "./ResponsiveSheet";

interface QuickAddThoughtModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (body: string, title?: string) => void;
}

export function QuickAddThoughtModal({ open, onClose, onAdd }: QuickAddThoughtModalProps) {
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");

  const handleSubmit = () => {
    if (!body.trim() || body.length > 50000 || title.length > 255) return;
    onAdd(body.trim(), title.trim() || undefined);
    setBody("");
    setTitle("");
    onClose();
  };

  const handleClose = () => {
    setBody("");
    setTitle("");
    onClose();
  };

  return (
    <ResponsiveSheet
      open={open}
      onClose={handleClose}
      label="New thought"
      desktop="center"
      desktopWidth="max-w-[440px]"
      zIndex={60}
    >
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-accent" />
                  <h2
                    className="text-primary"
                    style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "1.05rem", fontWeight: 400 }}
                  >
                    New thought
                  </h2>
                </div>
                <button
                  onClick={handleClose}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="space-y-3">
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Title (optional)"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border/30 bg-background text-foreground placeholder:text-muted-foreground/30 outline-none focus:border-accent/30 transition-colors"
                  style={{ fontSize: "0.88rem", fontFamily: "'Fraunces', Georgia, serif" }}
                  autoFocus
                />
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="What's on your mind?"
                  rows={3}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border/30 bg-background text-foreground placeholder:text-muted-foreground/30 outline-none focus:border-accent/30 resize-none transition-colors"
                  style={{ fontSize: "0.85rem", lineHeight: "1.6" }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />
              </div>

              <div className="flex items-center justify-between mt-4">
                <span className="hint-keyboard text-muted-foreground/40" style={{ fontSize: "0.72rem" }}>
                  Ctrl+Enter to save
                </span>
                <button
                  onClick={handleSubmit}
                  disabled={!body.trim()}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl transition-all ${
                    body.trim()
                      ? "bg-accent text-accent-foreground hover:opacity-90"
                      : "bg-muted/20 text-muted-foreground/30 cursor-not-allowed"
                  }`}
                  style={{ fontSize: "0.85rem" }}
                >
                  Save thought
                </button>
              </div>
            </div>
    </ResponsiveSheet>
  );
}
